import React from "react"
import PropTypes from "prop-types"

import { Link, Trans } from "gatsby-plugin-react-i18next"

import PathTransformer from "./parsers/PathTransformer"

import * as topMenuExtendedStyles from "../styles/TopMenuExtended.module.css"

const isInternal = menuItem => {
  let isInternal = true

  if (menuItem.path) {
    isInternal = /^\/(?!\/)/.test(menuItem.path)
  }

  return isInternal
}

const flatListToHierarchical = (
  data = [],
  rootMenuId,
  { idKey = "id", parentKey = "parentId", childrenKey = "children" } = {},
) => {
  const tree = []
  const childrenOf = {}
  data.forEach(item => {
    const newItem = { ...item }

    newItem.isInternal = isInternal(newItem)

    const { [idKey]: id, [parentKey]: parentId = 0 } = newItem
    childrenOf[id] = childrenOf[id] || []
    newItem[childrenKey] = childrenOf[id]
    parentId && parentId !== rootMenuId
      ? (childrenOf[parentId] = childrenOf[parentId] || []).push(newItem)
      : tree.push(newItem)
  })
  return tree
}

const subMenuItemInPath = (hierarchicalMenuItems, subMenuItem) => {
  const filteredItem = hierarchicalMenuItems.filter(mi => mi.id === subMenuItem)
  if (filteredItem.length && filteredItem[0]) {
    return filteredItem[0]
  }
  return null
}

const subMenuItemChildren = (hierarchicalMenuItems, subMenuItem) => {
  const filteredItem = hierarchicalMenuItems.filter(mi => mi.id === subMenuItem)
  if (filteredItem.length && filteredItem[0]) {
    return filteredItem[0].children
  }
  return []
}

const subMenuCustomEntries = item => {
  let entries = []

  const subMenuEntries =
    (item.connectedNode &&
      item.connectedNode.node &&
      item.connectedNode.node.subMenuEntries) ||
    []

  const menuPages = Object.keys(subMenuEntries).sort()

  entries = menuPages
    .filter(
      p =>
        subMenuEntries[p].subMenuPageName && subMenuEntries[p].subMenuPageLink,
    )
    .map(p => ({
      name: subMenuEntries[p].subMenuPageName,
      path:
        subMenuEntries[p].subMenuPageLink &&
        subMenuEntries[p].subMenuPageLink.uri,
    }))

  return entries
}

const subMenuCustomExternalEntries = item => {
  let entries = []

  const subMenuEntries =
    (item.connectedNode &&
      item.connectedNode.node &&
      item.connectedNode.node.subMenuExternalEntries) ||
    []

  const menuPages = Object.keys(subMenuEntries).sort()

  entries = menuPages
    .filter(
      p =>
        subMenuEntries[p].subMenuLinkName && subMenuEntries[p].subMenuLinkUrl,
    )
    .map(p => ({
      name: subMenuEntries[p].subMenuLinkName,
      path: subMenuEntries[p].subMenuLinkUrl,
    }))

  return entries
}

const connectedNodeId = src => {
  return (
    src.connectedNode && src.connectedNode.node && src.connectedNode.node.id
  )
}

const TopMenuExtended = ({
  rootMenuId,
  rootMenuConnectedId,
  midLevelMenuId,
  menuId,
  label,
  excerpt,
  childMenuItems,
  pageContext,
  className,
  onItemSelected,
}) => {
  const hierarchicalMenuItems = flatListToHierarchical(
    childMenuItems,
    rootMenuId,
  )
  const isMultipleLevel = hierarchicalMenuItems.reduce(
    (prev, menuItem) => prev || menuItem.children.length !== 0,
    false,
  )

  childMenuItems = childMenuItems.map(i => {
    i.isInternal = isInternal(i)
    return i
  })

  const [subMenuItem, setSubMenuItem] = React.useState(null)
  const prevRootMenuId = React.useRef()

  React.useEffect(() => {
    if (!isMultipleLevel) {
      prevRootMenuId.current = rootMenuId
      setSubMenuItem(null)
    } else {
      if (rootMenuId !== prevRootMenuId.current) {
        prevRootMenuId.current = rootMenuId
        if (subMenuItemInPath(hierarchicalMenuItems, midLevelMenuId)) {
          setSubMenuItem(midLevelMenuId)
        } else {
          setSubMenuItem(hierarchicalMenuItems[0].id)
        }
      }
    }
  }, [
    rootMenuId,
    midLevelMenuId,
    menuId,
    isMultipleLevel,
    hierarchicalMenuItems,
  ])

  const renderedExcerpt = excerpt || `<h2>${label}</h2>`

  const subSubMenuItems =
    isMultipleLevel && subMenuItem
      ? subMenuItemChildren(hierarchicalMenuItems, subMenuItem)
      : childMenuItems

  return (
    <div className={`row ${className} ${topMenuExtendedStyles.wrapper}`}>
      {!isMultipleLevel && (
        <div
          className={`col-3 ${topMenuExtendedStyles.navigator}`}
          dangerouslySetInnerHTML={{ __html: renderedExcerpt }}
        />
      )}
      {isMultipleLevel && (
        <div className={`col-3 ${topMenuExtendedStyles.navigator}`}>
          {hierarchicalMenuItems.map((menuItem, idx) => (
            <a
              key={idx}
              href="#!"
              className={`${
                menuItem.id === subMenuItem
                  ? topMenuExtendedStyles.navigatorActive
                  : ``
              }`}
              onClick={evt => {
                evt.preventDefault()
                setSubMenuItem(menuItem.id)
              }}
            >
              {menuItem.id === subMenuItem && (
                <div
                  className={`${topMenuExtendedStyles.menuSelector}`}
                  dangerouslySetInnerHTML={{
                    __html:
                      menuItem.connectedNode.node.excerpt ||
                      `<p>${menuItem.label}</p>`,
                  }}
                />
              )}
              {menuItem.id !== subMenuItem && (
                <div
                  className=""
                  dangerouslySetInnerHTML={{
                    __html: `<h2>${menuItem.label}</h2>`,
                  }}
                />
              )}
            </a>
          ))}
        </div>
      )}

      <div className={`col-9 ${topMenuExtendedStyles.menuItens}`}>
        <div className="row">
          {subSubMenuItems &&
            subSubMenuItems.map((child, idx) => (
              <div key={idx} className="col-3">
                {child.isInternal && (
                  <Link
                    className={`${topMenuExtendedStyles.menuLink} ${
                      child.id === menuId
                        ? topMenuExtendedStyles.menuLinkActive
                        : ``
                    }`}
                    to={PathTransformer(
                      child.path,
                      "en_gb", // HACK: pageContext.language,
                      pageContext.i18n.defaultLanguage,
                      pageContext.i18n.languages,
                    )}
                    language="en_gb"
                    onClick={evt => onItemSelected && onItemSelected(child.id)}
                  >
                    <Trans>{child.label}</Trans>
                    {rootMenuConnectedId !== connectedNodeId(child) && (
                      <p
                        className={`${topMenuExtendedStyles.excerpt}`}
                        dangerouslySetInnerHTML={{
                          __html:
                            (child.connectedNode &&
                              child.connectedNode.node &&
                              child.connectedNode.node.excerpt) ||
                            ``,
                        }}
                      />
                    )}
                  </Link>
                )}
                {subMenuCustomEntries(child).map((entry, eindx) => (
                  <Link
                    key={eindx}
                    className={`${topMenuExtendedStyles.submenuLink}`}
                    to={PathTransformer(
                      entry.path,
                      'en_gb', // HACK: pageContext.language,
                      pageContext.i18n.defaultLanguage,
                      pageContext.i18n.languages,
                    )}
                    language="en_gb"
                  >
                    {entry.name}
                  </Link>
                ))}
                {subMenuCustomExternalEntries(child).map((entry, eindx) => (
                  <a
                    key={eindx}
                    href={entry.path}
                    target="_blank"
                    rel="noreferrer"
                    className={`${topMenuExtendedStyles.submenuLink}`}
                  >
                    {entry.name}
                  </a>
                ))}
                {!child.isInternal && (
                  <a
                    href={child.path}
                    target="_blank"
                    className={`${topMenuExtendedStyles.menuLink} ${
                      child.id === menuId
                        ? topMenuExtendedStyles.menuLinkActive
                        : ``
                    }`}
                  >
                    <Trans>{child.label}</Trans>
                    <p
                      className={`${topMenuExtendedStyles.excerpt}`}
                      dangerouslySetInnerHTML={{
                        __html:
                          (child.connectedNode &&
                            child.connectedNode.node &&
                            child.connectedNode.node.excerpt) ||
                          ``,
                      }}
                    ></p>
                  </a>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default TopMenuExtended

TopMenuExtended.propTypes = {
  rootMenuId: PropTypes.string.isRequired,
  rootMenuConnectedId: PropTypes.string,
  midLevelMenuId: PropTypes.string,
  menuId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  childMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      parentId: PropTypes.string,
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      connectedNode: PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          excerpt: PropTypes.string,
        }),
      }),
    }).isRequired,
  ),
  onItemSelected: PropTypes.func,
}
